import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Loader, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { useQueryClient } from '@tanstack/react-query';

import { useGetTripRelations } from '../../../hooks/useIncidentApi';
import { AccountFieldStatic } from '../fields/AccountField';
import { AssigneeIdField } from '../fields/AssigneeIdField';
import { ClassificationIdField } from '../fields/ClassificationIdField';
import { DescriptionField } from '../fields/DescriptionField';
import { ImpactedUserIdsField } from '../fields/ImpactedUserIdsField';
import { IncidentDateField } from '../fields/IncidentDateField';
import { KustomerUrlField } from '../fields/KustomerUrlField';
import { OffenderIdField } from '../fields/OffenderIdField';
import { OffenderTypeField } from '../fields/OffenderTypeField';
import { ReportedOnField } from '../fields/ReportedOnField';
import { TripIdField } from '../fields/TripIdField';
import {
  CloseButton,
  IncidentDetailsContainer,
  StyledDrawer,
  StyledForm,
  StyledModalActions,
} from '../IncidentForm.styles';

import { addIncidentValidationSchema } from './validationSchema';

export interface AddIncidentFormValues {
  reportedOn: string | Date | null;
  kustomerUrl: string;
  assigneeId: number | null;
  offenderType: string;
  description: string;
  classificationId: number;
  subclassificationId: number | null;
  tripId: number | null;
  incidentDate: string | Date | null;
  offenderId: number | null;
  impactedUserIds: Array<number>;
}

export interface AddIncidentFormProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: AddIncidentFormValues) => void;
}

const AddIncidentForm = ({ isOpen, isSubmitting, onClose, onSubmit }: AddIncidentFormProps): ReactElement => {
  const [rideSlugInput, setRideSlugInput] = useState<string>('');
  const queryClient = useQueryClient();
  const {
    data: tripResponse,
    error: tripError,
    isSuccess: isTripSuccess,
    isFetching: isTripFetching,
  } = useGetTripRelations(rideSlugInput);

  const methods = useForm<AddIncidentFormValues>({
    defaultValues: {
      reportedOn: new Date(),
    },
    resolver: yupResolver(addIncidentValidationSchema),
  });

  const currentOffenderType = methods.watch('offenderType');

  useEffect(() => {
    if (!isOpen) {
      methods.reset();
      methods.setValue('offenderType', '');
      methods.setValue('description', '');
      methods.setValue('kustomerUrl', '');
      setRideSlugInput('');
      queryClient.resetQueries({ queryKey: ['getTripRelations'] });
    }
  }, [isOpen]);

  return (
    <StyledDrawer open={isOpen} anchor="right">
      <Modal.Title>
        <Box display="flex" justifyContent="space-between">
          <Text variant="h6">Add T&S Incident Form</Text>
          <CloseButton onClick={onClose}>
            <Svg height="20px" icon="cross" />
          </CloseButton>
        </Box>
      </Modal.Title>

      <Modal.Content>
        <FormProvider {...methods}>
          <StyledForm id="add-incident-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <ReportedOnField />
            <KustomerUrlField />
            <AssigneeIdField />
            <OffenderTypeField />

            {currentOffenderType && (
              <IncidentDetailsContainer>
                <ClassificationIdField />

                <TripIdField
                  trip={tripResponse}
                  isLoading={isTripFetching}
                  error={tripError?.errors ? tripError?.errors[0] : ''}
                  inputValue={rideSlugInput}
                  onInputChange={setRideSlugInput}
                />

                {tripResponse && isTripSuccess && (
                  <>
                    <AccountFieldStatic defaultValue={tripResponse.account} />
                    <IncidentDateField defaultValue={tripResponse.startsAt} />
                    <OffenderIdField
                      currentOffenderType={currentOffenderType}
                      riders={tripResponse.riders || []}
                      drivers={tripResponse.drivers || []}
                      carePartnerDrivers={tripResponse.carePartnerDrivers || []}
                      riderAssistants={tripResponse.riderAssistants || []}
                    />
                    <ImpactedUserIdsField
                      currentOffenderType={currentOffenderType}
                      riders={tripResponse.riders || []}
                      drivers={tripResponse.drivers || []}
                      carePartnerDrivers={tripResponse.carePartnerDrivers || []}
                      riderAssistants={tripResponse.riderAssistants || []}
                    />
                  </>
                )}
              </IncidentDetailsContainer>
            )}

            <DescriptionField />
          </StyledForm>
        </FormProvider>
      </Modal.Content>
      <StyledModalActions>
        <Divider />
        <Box className="buttons">
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button form="add-incident-form" type="submit" color="primary" disabled={isSubmitting}>
            Add {isSubmitting && <Loader className="loader" size={12} />}
          </Button>
        </Box>
      </StyledModalActions>
    </StyledDrawer>
  );
};

export default AddIncidentForm;
